import ReactDOM from "react-dom";
import { NoteInformation } from "../../features/subscriptions/presentation/components/CardInformation";
import { SendMessage } from "../icons/SendMessage";
import { Column } from "./Column";
import { CustomButton } from "./CustomButton";
import { CustomInput } from "./CustomInput";
import { ModalSimple } from "./Modal";
import { Row } from "./Row";
import { TextComponent } from "./TextComponent";
import useDirectMessageActions from "../../features/match/presentation/hook/useDirectMessageActions";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import { updateMyNotificationReceived } from "../../features/match/presentation/store/MatchSlice";
import { List } from "../../features/subscriptions/presentation/components/List";
import { Likes } from "../../features/match/domain/entities/userMatch";
import useMatch from "../../features/match/presentation/hook/useMatch";
import { apiServices } from "../utilities/ApiServices";

const maxLength = 160;

interface PreMatchMessageBalance {
	included_pre_match_messages_remaining: number;
	returned_pre_match_messages_remaining?: number;
	purchased_pre_match_messages_remaining: number;
	total_pre_match_messages_remaining: number;
}

const safeBalanceCount = (value: unknown) => {
	const count = Number(value);
	return Number.isFinite(count) && count >= 0 ? Math.floor(count) : 0;
};

export const PurchaseMessage = ({
	username,
	user_id,
	notification,
}: {
	username: string;
	user_id: string;
	notification?: {
		userInfoProfile: Partial<Likes | undefined>;
		setUserInfoProfile: React.Dispatch<React.SetStateAction<Partial<Likes | undefined>>>;
		activeCategory: "like" | "match" | "alert" | "myLikes";
	};
}) => {
	const dispatch: AppDispatch = useDispatch();
	const { user } = useSelector((state: RootState) => state.onboarding);
	const [hasCredits, setHasCredits] = useState<boolean>(false);
	const [inputValue, setInputValue] = useState<number>(1);
	const [isSendMessageDirect, setIsSendMessageDirect] = useState<boolean>(false);
	const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value);
	const [text, setText] = useState<string>("");
	const [messageBalance, setMessageBalance] = useState<PreMatchMessageBalance>();
	const balanceRequestGeneration = useRef(0);
	const { handleUpdateMatchesSearch } = useMatch();
	const refreshMessageBalance = useCallback(async () => {
		const requestGeneration = ++balanceRequestGeneration.current;
		const requestSession = window.localStorage.getItem("userToken");
		try {
			const response = await apiServices.get<PreMatchMessageBalance>("/match/message-balance");
			if (
				requestGeneration !== balanceRequestGeneration.current ||
				requestSession !== window.localStorage.getItem("userToken")
			) return;
			if (response.status !== 200) return;
			setMessageBalance({
				included_pre_match_messages_remaining: safeBalanceCount(response.data.included_pre_match_messages_remaining),
				returned_pre_match_messages_remaining: safeBalanceCount(response.data.returned_pre_match_messages_remaining),
				purchased_pre_match_messages_remaining: safeBalanceCount(response.data.purchased_pre_match_messages_remaining),
				total_pre_match_messages_remaining: safeBalanceCount(response.data.total_pre_match_messages_remaining),
			});
		} catch {
			// Keep the member's existing purchased-credit count as a safe fallback.
		}
	}, []);

	useEffect(() => {
		void refreshMessageBalance();
		return () => {
			balanceRequestGeneration.current += 1;
		};
	}, [refreshMessageBalance]);

	const availableMessageCount = messageBalance?.total_pre_match_messages_remaining
		?? safeBalanceCount(user?.messages_count);
	const { isLoadingPayMessage, errorSendMessageDirect, directMessageError, errorNumberMessage, handleSendDirectMessage, handlePayForMessage, handleInputChange, handleDirectMessage, handleSendMessage, handleKeyDownTextarea } =
		useDirectMessageActions({
			user,
			text,
			setText,
			setInputValue,
			availableMessageCount,
			onMessageSent: refreshMessageBalance,
			setHasCredits,
			setIsSendMessageDirect,
			...(!notification && { handleUpdateMatchesSearch }),
		});

	const itemsList1 = [
		{
			text: "Instant Messages Before Matching",
			subtext: "Paid members receive five included uses in every paid subscription month.",
		},
		{
			text: "Monthly Refresh",
			subtext: "Unused included messages do not roll over to the next paid month.",
		},
		{
			text: "Purchased Credits Stay Available",
			subtext: "Separately purchased credits are kept alongside the monthly allowance.",
		},
	];

	const handleSendDirectMessageNotification = async () => {
		const sent = await handleSendDirectMessage(user_id);
		if (!sent) return;
		if (!notification) return;
		dispatch(
			updateMyNotificationReceived({
				type: notification.activeCategory,
				userMyNotification: { ...notification.userInfoProfile, message_text: text },
			})
		);
		notification.setUserInfoProfile((prevProfile) => ({
			...prevProfile,
			message_text: text,
		}));
	};

	return (
		<>
			{ReactDOM.createPortal(
				<>
					{((isSendMessageDirect && availableMessageCount !== 0) || (!isSendMessageDirect && hasCredits && availableMessageCount === 0)) && (
						<ModalSimple
							title={`${isSendMessageDirect ? `Send Direct Message to ${username}` : "Buy messages"}`}
							variant="secondary"
							enableButton={false}
							actionExecuted={isSendMessageDirect || hasCredits}
							icon={<SendMessage size={80} />}
							onClose={() => (isSendMessageDirect ? setIsSendMessageDirect(false) : setHasCredits(false))}
						>
							<Column className="gap-3 p-2 text-start">
								{isSendMessageDirect ? (
									<TextComponent variant="nh6" className="text-center">One Instant Message will be deducted if you confirm this action.</TextComponent>
								) : (
									<>
										{itemsList1.map((item, index) => (
											<List
												key={index}
												text={item.text}
												subtext={item.subtext}
											/>
										))}
                                        <Column className="gap-2">
                                            <CustomInput
                                                label="Enter the number of messages"
                                                className="gap-2 text-center text-black font-semibold"
                                                type="number"
                                                onChange={handleInputChange}
                                                value={inputValue === 0 ? "" : inputValue.toString()}
                                                min={1}
                                                max={10}
                                                small
                                            />
                                            {errorNumberMessage &&
                                                <TextComponent variant="h7" className="text-red-500">
                                                    {errorNumberMessage}
                                                </TextComponent>
                                            }
                                        </Column>
										<NoteInformation
											title="Note:"
											description="You can send one introduction per member. If they reply, the conversation can continue normally."
											className="bg-grayDefault text-graySecondary"
										/>
									</>
								)}
								<Row className="w-full gap-3">
									<CustomButton
										type="button"
										className="w-1/2"
										color="white"
										onClick={() => (isSendMessageDirect ? setIsSendMessageDirect(false) : setHasCredits(false))}
										small
									>
										{isSendMessageDirect ? "Cancel" : "Close"}
									</CustomButton>
									<CustomButton
										type="submit"
										className="w-1/2"
										onClick={isSendMessageDirect ? handleSendDirectMessageNotification : () => handlePayForMessage("ca5fb77b-162f-4627-8c12-321932caef7a", inputValue)}
										loading={isLoadingPayMessage}
										small
									>
										{isSendMessageDirect ? "Accept" : "Pay for Message"}
									</CustomButton>
								</Row>
							</Column>
						</ModalSimple>
					)}
				</>,
				document.getElementById("root") as HTMLElement
			)}
			{availableMessageCount !== 0 && (
				<TextComponent
					variant="nh6"
					className="font-bold"
				>
					You have <span className="text-primary">{availableMessageCount} Instant Message(s)</span> remaining.
				</TextComponent>
			)}
			{messageBalance && availableMessageCount !== 0 && (
				<TextComponent variant="nh6" className="text-graySecondary">
					Included this month: {messageBalance.included_pre_match_messages_remaining}. Purchased: {messageBalance.purchased_pre_match_messages_remaining}.{(messageBalance.returned_pre_match_messages_remaining || 0) > 0 && <> Returned: {messageBalance.returned_pre_match_messages_remaining}.</>}
				</TextComponent>
			)}
			<TextComponent
				variant="nh6"
				className="text-graySecondary"
			>
					Send a short introduction before matching. You can send one Instant Message per member.
			</TextComponent>
			<textarea
				placeholder="Write a message"
				className="w-full text-sm resize-none rounded-lg border border-grayLight px-4 py-4 placeholder:text-grayLight2 placeholder:select-none outline-none"
				maxLength={maxLength}
				value={text}
				onChange={handleChange}
				onClick={handleDirectMessage}
				onKeyDown={handleKeyDownTextarea}
				readOnly={availableMessageCount === 0}
			/>
			<TextComponent
				variant="nh6"
				className="text-right text-graySecondary"
			>
				{text.length}/{maxLength}
			</TextComponent>
			{errorSendMessageDirect && (
				<TextComponent
					variant="nh6"
					className="text-left text-red-500"
				>
					This field is required
				</TextComponent>
			)}
			{directMessageError && (
				<TextComponent
					variant="nh6"
					className="text-left text-red-500"
				>
					{directMessageError}
				</TextComponent>
			)}
			{availableMessageCount !== 0 && <CustomButton onClick={handleSendMessage}>Send Message</CustomButton>}
		</>
	);
};
